// ===========================================================================
// Charge Capture — Capa de servicio (FAKE, contrato real).
//
// Cada función replica el endpoint previsto del Módulo N
// (docs/modulos/BillingV2/MODULO_N_CHARGE_CAPTURE.md — schema `billing`,
// tablas billing.TAB_CHARGES / TAB_CHARGE_RECOMMENDATIONS /
// TAB_CHARGE_VALIDATIONS / TAB_MISSING_CHARGES). Hoy resuelven contra
// common/mockData.js; el swap al backend es 1:1 por función.
// ===========================================================================
import {
  MOCK_CHARGES,
  MOCK_MISSING_CHARGES,
  MOCK_METRICS,
  MOCK_PROC_CATALOG,
  MOCK_DIAG_CATALOG,
  MOCK_OBSERVATIONS,
} from "../common/mockData";

// Estado mutable de la sesión: decisiones del coder sobre los mocks.
const session = {
  charges: new Map(
    MOCK_CHARGES.map((c) => [
      c.chargeId,
      {
        ...JSON.parse(JSON.stringify(c)),
        observations: JSON.parse(JSON.stringify(MOCK_OBSERVATIONS[c.chargeId] || [])),
      },
    ])
  ),
  missingResolved: new Set(),
};

let manualSeq = 100;
const nextManualId = (prefix) => `${prefix}-m${++manualSeq}`;

const latency = (ms = 350) => new Promise((r) => setTimeout(r, ms));

/**
 * Cola de charges a revisar (N.1 — Charge Slip Recommendation Engine).
 * Encounters con nota cerrada y charge slip recomendado pendiente de revisión.
 *
 * FUTURO ENDPOINT: GET /api/charges?status=RECOMMENDED,IN_REVIEW
 *
 * Respuesta exitosa (200):
 * [ { "chargeId": "CHG-2026-00187", "patientName": "...", "dateOfService":
 *     "2026-05-26", "status": "RECOMMENDED", "confidenceOverall": 92,
 *     "estimatedRevenue": 425.50, "hccsCaptured": 2, ... } ]
 *
 * Posibles errores (400): { "result": 1, "message": "Invalid status filter" }
 */
export async function getChargeQueue() {
  await latency();
  return Array.from(session.charges.values());
}

/**
 * Detalle completo de un charge: nota clínica fuente, diagnósticos y
 * procedimientos recomendados con evidencia MEAT, y validaciones (N.2).
 *
 * FUTURO ENDPOINT: GET /api/charges/{chargeId}
 * Posibles errores (404): { "result": 1, "message": "Charge not found" }
 */
export async function getChargeById(chargeId) {
  await latency(250);
  const charge = session.charges.get(chargeId);
  if (!charge) throw new Error(`Charge ${chargeId} not found`);
  return charge;
}

/**
 * Actualiza decisiones del coder sobre el charge slip: aceptar/rechazar un
 * diagnóstico o procedimiento recomendado.
 *
 * FUTURO ENDPOINT: PUT /api/charges/{chargeId}
 * Body: { "diagnoses": [{ "id": "dx-1", "accepted": true }], ... }
 */
export async function updateChargeDecision(chargeId, { itemType, itemId, accepted }) {
  await latency(150);
  const charge = session.charges.get(chargeId);
  if (!charge) throw new Error(`Charge ${chargeId} not found`);
  const list = itemType === "diagnosis" ? charge.diagnoses : charge.procedures;
  const item = list.find((x) => x.id === itemId);
  if (item) item.accepted = accepted;
  if (charge.status === "RECOMMENDED") charge.status = "IN_REVIEW";
  return { ...charge };
}

/**
 * Aprueba el charge slip y lo empuja a AdvancedMD (N.4 — Charge
 * Synchronization). Detrás: lookupproccode → savecharges (Tier 2) →
 * verificación con getchargedetaildata.
 *
 * FUTURO ENDPOINT: POST /api/charges/{chargeId}/approve
 *
 * Respuesta exitosa (200):
 * { "chargeId": "CHG-2026-00187", "status": "SYNCED_AMD",
 *   "syncedAt": "2026-06-12T15:04:00Z" }
 *
 * Posibles errores (422): Fault de AdvancedMD (procId inválido, visita
 * cerrada); (429) rate limit Tier 2 excedido.
 */
export async function approveAndSync(chargeId) {
  await latency(700);
  const charge = session.charges.get(chargeId);
  if (!charge) throw new Error(`Charge ${chargeId} not found`);
  charge.status = "SYNCED_AMD";
  charge.syncedAt = new Date().toISOString();
  return { ...charge };
}

/**
 * Missing charges (N.3 — Missing Charges Detector): encounters completados
 * con nota pero sin charge, con severidad por horas transcurridas.
 *
 * FUTURO ENDPOINT: GET /api/charges/missing
 *
 * Respuesta exitosa (200):
 * { "summary": { "missingCount": 12, "revenueAtRisk": 3800, ... },
 *   "items": [ { "id": "miss-1", "patientName": "...", "hoursElapsed": 96,
 *                "severity": "CRITICAL" } ] }
 */
export async function getMissingCharges() {
  await latency();
  return {
    summary: MOCK_MISSING_CHARGES.summary,
    items: MOCK_MISSING_CHARGES.items.map((i) => ({
      ...i,
      resolved: session.missingResolved.has(i.id),
    })),
  };
}

/**
 * Marca un missing charge como resuelto (se creó el charge manualmente o se
 * justificó la ausencia).
 *
 * FUTURO ENDPOINT: POST /api/charges/missing/{id}/resolve
 */
export async function resolveMissingCharge(missingId) {
  await latency(200);
  session.missingResolved.add(missingId);
  return { id: missingId, resolved: true };
}

/**
 * Métricas del mes del módulo (capture rate, HCCs adicionales, revenue,
 * desempeño por provider).
 *
 * FUTURO ENDPOINT: GET /api/charges/metrics?month=2026-05
 */
export async function getMetrics() {
  await latency(300);
  return MOCK_METRICS;
}

/**
 * Búsqueda de códigos CPT para ingreso manual. OBLIGATORIA antes de agregar
 * un procedimiento: savecharges necesita el ID INTERNO (procId), no el CPT.
 *
 * FUTURO ENDPOINT: GET /api/charges/proc-codes?q=99213
 * (detrás: lookupproccode de AdvancedMD, con cache local de frecuentes)
 *
 * Respuesta exitosa (200):
 * [ { "procId": 70023, "cpt": "99214", "description": "Office visit..." } ]
 */
export async function lookupProcCodes(query = "") {
  await latency(180);
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return MOCK_PROC_CATALOG.filter(
    (p) => p.cpt.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)
  );
}

/**
 * Búsqueda de diagnósticos ICD-10 para ingreso manual.
 *
 * FUTURO ENDPOINT: GET /api/charges/diag-codes?q=e11
 * (detrás: lookupdiagcode / lookupdiagnosiscode)
 */
export async function lookupDiagCodes(query = "") {
  await latency(180);
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return MOCK_DIAG_CATALOG.filter(
    (d) => d.icd10.toLowerCase().includes(q) || d.description.toLowerCase().includes(q)
  );
}

/**
 * Agrega al charge slip un diagnóstico ingresado manualmente por el coder
 * (source MANUAL, a diferencia de los RECOMMENDED del engine N.1).
 *
 * FUTURO ENDPOINT: POST /api/charges/{chargeId}/diagnoses
 * Body: { "icd10": "E11.42" }
 * Posibles errores (409): { "result": 1, "message": "Diagnosis already on charge" }
 */
export async function addChargeDiagnosis(chargeId, catalogEntry) {
  await latency(200);
  const charge = session.charges.get(chargeId);
  if (!charge) throw new Error(`Charge ${chargeId} not found`);
  if (charge.diagnoses.some((d) => d.icd10 === catalogEntry.icd10)) {
    throw new Error(`${catalogEntry.icd10} is already on this charge`);
  }
  charge.diagnoses.push({
    id: nextManualId("dx"),
    icd10: catalogEntry.icd10,
    description: catalogEntry.description,
    rank: charge.diagnoses.length + 1,
    source: "MANUAL",
    confidence: null,
    hccCode: catalogEntry.hccCode || null,
    rafContribution: 0,
    codingOpportunityId: null,
    meatEvidence: "Added manually by the coder — verify MEAT support in the note before pushing.",
    accepted: true,
  });
  if (charge.status === "RECOMMENDED") charge.status = "IN_REVIEW";
  return { ...charge };
}

/**
 * Agrega al charge slip un procedimiento ingresado manualmente por el coder.
 *
 * FUTURO ENDPOINT: POST /api/charges/{chargeId}/procedures
 * Body: { "procId": 70023, "units": 1, "modifiers": "", "linkedIcd10": "E11.22" }
 */
export async function addChargeProcedure(chargeId, catalogEntry) {
  await latency(200);
  const charge = session.charges.get(chargeId);
  if (!charge) throw new Error(`Charge ${chargeId} not found`);
  if (charge.procedures.some((p) => p.cpt === catalogEntry.cpt)) {
    throw new Error(`CPT ${catalogEntry.cpt} is already on this charge`);
  }
  const firstDx = charge.diagnoses.find((d) => d.accepted);
  charge.procedures.push({
    id: nextManualId("cpt"),
    cpt: catalogEntry.cpt,
    procId: catalogEntry.procId,
    description: catalogEntry.description,
    modifiers: "",
    units: 1,
    linkedIcd10: firstDx ? firstDx.icd10 : "",
    source: "MANUAL",
    confidence: null,
    careGapId: null,
    evidence: "Added manually by the coder.",
    accepted: true,
  });
  if (charge.status === "RECOMMENDED") charge.status = "IN_REVIEW";
  return { ...charge };
}

/**
 * Agrega una observación del coder al hilo del charge (chat coder ↔ médico).
 * El hilo reemplaza el ida y vuelta por teléfono/email para acordar ajustes
 * de la documentación o del charge slip antes del push.
 *
 * FUTURO ENDPOINT: POST /api/charges/{chargeId}/observations
 * Body: { "text": "..." }  → persiste en billing.TAB_CHARGE_OBSERVATIONS y
 * notifica al provider (portal/email). El provider responde desde su vista.
 */
export async function addObservation(chargeId, text) {
  await latency(200);
  const charge = session.charges.get(chargeId);
  if (!charge) throw new Error(`Charge ${chargeId} not found`);
  charge.observations = charge.observations || [];
  charge.observations.push({
    id: nextManualId("obs"),
    author: "coder",
    authorName: "You (Coder)",
    text,
    at: new Date().toISOString(),
  });
  return { ...charge };
}

// Respuestas simuladas del médico, solo para el prototipo: en producción la
// respuesta llega del provider real vía el endpoint de observaciones.
const SIMULATED_PROVIDER_REPLIES = [
  "Thanks for flagging it — I reviewed the note and will addend it today so the code is fully supported.",
  "Agreed. Go ahead and adjust the charge slip as you suggest; the clinical picture supports it.",
  "Good catch. Let's keep the more specific code — the documentation in the A/P section supports it.",
  "I'd rather not bill that one this visit; documentation is incomplete. I'll address it at the follow-up.",
];

/**
 * Simula la respuesta del médico en el hilo (SOLO prototipo). Determinística
 * por longitud del hilo para que la demo sea estable.
 */
export async function simulateProviderReply(chargeId) {
  await latency(1600);
  const charge = session.charges.get(chargeId);
  if (!charge) throw new Error(`Charge ${chargeId} not found`);
  charge.observations = charge.observations || [];
  const reply =
    SIMULATED_PROVIDER_REPLIES[charge.observations.length % SIMULATED_PROVIDER_REPLIES.length];
  charge.observations.push({
    id: nextManualId("obs"),
    author: "provider",
    authorName: `${charge.providerName} (simulated)`,
    text: reply,
    at: new Date().toISOString(),
  });
  return { ...charge };
}
